import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Stack,
  Chip,
  Switch,
  Button,
  IconButton,
  Divider,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { managerDashboards } from "../../../config/managerDashboards";

const ACCENT = "#1976d2";

const getInitials = (user) => `${user.Name?.[0] || ""}${user.LastName?.[0] || ""}`.toUpperCase() || "?";

const EditManagerAccessDialog = ({ user, open, onClose, saving, onSave }) => {
  const [draft, setDraft] = useState({ isAdmin: false, allowedDashboards: [] });

  useEffect(() => {
    if (user) {
      setDraft({ isAdmin: user.isAdmin, allowedDashboards: user.allowedDashboards });
    }
  }, [user]);

  if (!user) return null;

  const toggleDashboard = (key) => {
    setDraft((prev) => ({
      ...prev,
      allowedDashboards: prev.allowedDashboards.includes(key)
        ? prev.allowedDashboards.filter((k) => k !== key)
        : [...prev.allowedDashboards, key],
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.75)} 100%)`,
          color: "#fff",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", width: 38, height: 38, fontWeight: 700 }}>
            {getInitials(user)}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              הרשאות דשבורד מנהלים
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {user.Name} {user.LastName}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#fafafa", p: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} color={ACCENT} sx={{ mb: 1 }}>
          רמת גישה
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Switch checked={draft.isAdmin} onChange={(e) => setDraft((prev) => ({ ...prev, isAdmin: e.target.checked }))} />
          <Typography variant="body2" fontWeight={500}>
            ADMIN — גישה לכל הדשבורדים
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} color={ACCENT} sx={{ mb: 1 }}>
          דשבורדים ספציפיים
        </Typography>
        <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ opacity: draft.isAdmin ? 0.4 : 1 }}>
          {managerDashboards.map((dashboard) => {
            const selected = draft.isAdmin || draft.allowedDashboards.includes(dashboard.key);
            return (
              <Chip
                key={dashboard.key}
                label={dashboard.label}
                clickable={!draft.isAdmin}
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                onClick={() => !draft.isAdmin && toggleDashboard(dashboard.key)}
              />
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fafafa" }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          ביטול
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          onClick={() => onSave(user, draft)}
          sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 600, background: ACCENT }}
        >
          {saving ? "שומר..." : "שמירה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditManagerAccessDialog;
